import * as React from 'react'
import cx from 'classnames'
import styles from './TabBar.sass'
import UnreadBadge from 'components/UnreadBadge'
import TabController from 'infra/TabController'
import Link from 'components/Link'

interface TabBarProps {
  children: React.ReactNode
  bordered?: boolean
  align?: 'center' | 'left' | 'right'
  stretch?: boolean
  centerText?: boolean
}

export function TabBar({
  children,
  bordered,
  align,
  stretch = false,
  centerText = false,
  ...rest
}: TabBarProps) {
  return (
    <div
      className={cx(
        styles.root,
        bordered && styles.bordered,
        align && styles[align],
        stretch && styles.stretch,
        centerText && styles.centerText
      )}
      {...rest}
    >
      {children}
    </div>
  )
}

interface TabItemProps {
  active?: boolean
  badgeCount?: number
  badgePosition?: 'top' | 'right'
  children: React.ReactNode
  onClick?: () => unknown
  to?: string
  dataTest?: string
  cardLayout?: boolean
}

export function TabItem({
  children,
  active,
  badgeCount,
  badgePosition = 'top',
  dataTest,
  cardLayout,
  ...rest
}: TabItemProps) {
  return (
    <Link
      {...rest}
      className={cx(
        styles.tab,
        active && styles.active,
        badgePosition === 'right' && styles.tabRightBadge,
        cardLayout && styles.sidePadding
      )}
      data-test={dataTest}
    >
      <div className={styles.text}>{children}</div>
      {badgeCount !== undefined && badgeCount > 0 && (
        <span
          className={
            badgePosition === 'top' ? styles.topBadge : styles.rightBadge
          }
        >
          <UnreadBadge count={badgeCount} />
        </span>
      )}
    </Link>
  )
}

/**
 * Hook for using a tab controller to render the tab bar children.
 *
 * @param controller
 */
export function useTabControllerForTabBar<T>(controller: TabController<T>) {
  const tabs = controller.tabs.map((tab) => (
    <TabItem
      key={String(tab.tab)}
      active={controller.current === tab}
      onClick={() => controller.change(tab.tab)}
      badgeCount={tab.hasError() ? 1 : 0}
    >
      {tab.title}
    </TabItem>
  ))
  return tabs
}
